import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from "../../../api/Base";
import { showError } from "../../../utils/showAlert";
import InstanceApi from '../../../api/instance/InstanceApi';

// Third-party packages.
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';

import { CSVLink } from 'react-csv';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Context.
import { EventTypeContext } from '../../../context/EventTypeContext';
import { InstanceContext } from "../../../context/InstanceContext";

// Types.
import InstancesAdminDataTable from '../../../types/instance/tables/InstancesAdminDataTable';
import InstanceTarget from "../../../types/instance/InstanceTarget";

// Constants.

// Helpers
import Helper from "../../../helpers/Helper";
import { getDefaultPageSize } from "../../../helpers/utils";

// Components.
import StatusIcon from '../../../component/StatusIcon';
import InstanceType from '../../../component/instance/InstanceType';
import ActionButtons from '../../../component/buttons/ActionButtons';
import NoData from '../../../component/NoData';
import TablePagination from '../../../component/TablePagination';
import SortClass from "../../../component/SortClass";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../../component/SearchInput';

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(relativeTime.default);
dayjs.extend(customParseFormat.default);

function List(props: { loading: number, instancesAdminDataTable: InstancesAdminDataTable[], isAdmin: boolean }) {
    const { instances, setInstances } = useContext(InstanceContext);
    const { eventTypes } = useContext(EventTypeContext);
    const [agents, setAgents] = useState<any[]>([]);
    const [instancesList, setInstancesList] = useState<InstancesAdminDataTable[]>([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());


    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        setInstancesList(props.instancesAdminDataTable);
        // Mark initial load complete once we receive actual data
        if (props.instancesAdminDataTable.length > 0) {
            setIsInitialLoad(false);
        }
    }, [props.instancesAdminDataTable]);
    
    // Fallback: if context loaded with instances OR after a delay (for empty state)
    useEffect(() => {
        if (instances.length > 0) {
            setIsInitialLoad(false);
        } else {
            // Give context time to load, then show empty state
            const timer = setTimeout(() => setIsInitialLoad(false), 1500);
            return () => clearTimeout(timer);
        }
    }, [instances]);

    const data = useMemo(() => instancesList.filter(row => (row.name.toLowerCase().includes(tableFilter.toLowerCase())) || (row.hostAddress.toLowerCase().includes(tableFilter.toLowerCase()))), [instancesList, tableFilter]);

    useEffect(() => {

        const getAgents = async () => {
            await api.get(`agent `)
                .then((response) => {
                    if (response) {
                        setAgents(response.data);
                    }
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve agents.', error);
                    showError('Failed to retrieve agents', error.message || error.toString());
                });
        }
        void getAgents();
    }, []);

    const removeInstance = (instanceId: number) => {
        const updatedInstancesList = instancesList.filter(instance => instance.id !== instanceId);
        setInstancesList(updatedInstancesList);
    }



    const duplicateInstance = async (instance: any) => {
        // Generate unique name with "copy" suffix
        const baseName = instance.instance.name;
        const existingNames = instances.map(inst => inst.name);
        let newName = `${baseName} copy`;
        let counter = 2;

        while (existingNames.includes(newName)) {
            newName = `${baseName} copy ${counter}`;
            counter++;
        }

        // Create duplicate data based on original instance
        const duplicateData = [{
            name: newName,
            agentid: instance.instance.agentid,
            type: instance.instance.type,
            database: instance.instance.database,
            server: instance.instance.server,
            debug: instance.instance.debug,
            host: instance.instance.host,
            port: instance.instance.port,
            username: instance.instance.username,
            password: null, 
            passwordchanged: false,
            driver: instance.instance.driver,
            monitor: false, // Start stopped
            url: instance.instance.url,
            useurl: instance.instance.useurl,
            parameters: instance.instance.parameters,
            monitoringintervalmilliseconds: instance.instance.monitoringintervalmilliseconds,
            batchintervalseconds: instance.instance.batchintervalseconds,
            detectchanges: instance.instance.detectchanges,
            collectobjectstatistics: instance.instance.collectobjectstatistics,
            collectsqlstatistics: instance.instance.collectsqlstatistics,
            collectdeadlocks: instance.instance.collectdeadlocks,
            collectblockingsessions: instance.instance.collectblockingsessions,
            collecttransactioncontention: instance.instance.collecttransactioncontention,
            collecttempdb: instance.instance.collecttempdb,
            collectjobhistory: instance.instance.collectjobhistory,
            explainlimit: instance.instance.explainlimit,
            encrypted: instance.instance.encrypted
        }];

        try {
            const originalInstanceId = instance.instance.id;
            
            let originalHostMappings: any[] = [];
            let originalLinks: any[] = [];
            let originalTags: any[] = [];
            
            try {
                const hostMappingsResponse = await api.get(`datasource/host?id=${originalInstanceId}`);
                if (hostMappingsResponse?.data) {
                    originalHostMappings = hostMappingsResponse.data;
                }
            } catch (error) {
                console.error('Failed to fetch host mappings:', error);
            }
            
            try {
                const linksResponse = await api.get(`datasource/link?datasourceid=${originalInstanceId}`);
                if (linksResponse?.data) {
                    originalLinks = linksResponse.data.filter((link: any) => link.datasourceid === originalInstanceId);
                }
            } catch (error) {
                console.error('Failed to fetch third party links:', error);
            }
            
            try {
                const tagsResponse = await api.get('datasource/tag');
                if (tagsResponse?.data) {
                    originalTags = tagsResponse.data.filter((tag: any) => tag.datasourceid === originalInstanceId);
                }
            } catch (error) {
                console.error('Failed to fetch tags:', error);
            }
            
            // Create the duplicate instance
            const result = await api.post('datasource', duplicateData);
            if (result.status === 200) {
                // Get the new instance ID
                let updatedInstances = await InstanceApi.getInstances();
                const newInstance = updatedInstances.find((inst: InstanceTarget) => inst.name === newName);
                
                if (newInstance) {
                    const newInstanceId = newInstance.id;
                    
                    // Copy host mappings
                    if (originalHostMappings.length > 0) {
                        try {
                            const newHostMappings = originalHostMappings.map((mapping: any) => ({
                                id: newInstanceId,
                                hostid: mapping.hostid
                            }));
                            await api.post('datasource/host', newHostMappings);
                        } catch (error) {
                            console.error('Failed to copy host mappings:', error);
                        }
                    }
                    
                    // Copy third party links
                    if (originalLinks.length > 0) {
                        try {
                            const newLinks = originalLinks.map((link: any) => ({
                                datasourceid: newInstanceId,
                                name: link.name,
                                url: link.url
                            }));
                            await api.post(`datasource/link?datasourceid=${newInstanceId}`, newLinks);
                        } catch (error) {
                            console.error('Failed to copy third party links:', error);
                        }
                    }
                    
                    // Copy tags
                    if (originalTags.length > 0) {
                        try {
                            const newTags = originalTags.map((tag: any) => ({
                                datasourceid: newInstanceId,
                                tagname: tag.tagname,
                                tagvalue: tag.tagvalue
                            }));
                            await api.post(`datasource/tag?datasourceid=${newInstanceId}`, newTags);
                        } catch (error) {
                            console.error('Failed to copy tags:', error);
                        }
                    }
                }
                
                // Refresh instances list
                updatedInstances = await InstanceApi.getInstances();
                setInstances(updatedInstances);
            }
        } catch (error: any) {
            console.error('Failed to duplicate instance:', error);
            showError('Failed to duplicate instance', error.message || error.toString());
        }
    }

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'instance',
            disableSortBy: true
        },
        {
            Header: 'Instance',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <StatusIcon instanceId={cell.row.values.id} removeFromList={removeInstance} mode="admin"/>
                        {cell.row.values.name}
                    </React.Fragment>
            )
        },
        {
            Header: '',
            accessor: 'type',
            headerClassName: 'text-center',
            className: 'text-center',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <InstanceType instance={cell.row.values.instance}/>
                    </React.Fragment>
            )
        },
        {
            Header: 'Host Address',
            accessor: 'hostAddress',
            className: 'concatenate',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    cell.row.values.hostAddress
            )
        },
        {
            Header: 'Agent',
            accessor: 'agent',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.instance.agentid - rowB.values.instance.agentid)
            },
            Cell: ({ cell }) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.instance.agentid)
                return getAgent ? getAgent.name : ''
            }
        },
        {
            Header: 'Agent Version',
            accessor: 'version',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.instance.agentid - rowB.values.instance.agentid)
            },
            Cell: ({ cell }) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.instance.agentid)
                return getAgent?.version ? getAgent.version : ''
            }
        },
        {
            Header: <div className='d-flex justify-content-between'><span className='w-25 text-start'>Last Contacted</span><span>Actions</span></div>,
            accessor: 'actions',
            className: 'actions w-10',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => {
                const isDeleted = isInstanceDeleted(cell.row.original)
                return isDeleted ? 'Deletion in progress' : <ActionButtons isAdmin={props.isAdmin} removeFromList={removeInstance} instance={cell.row.values.instance} eventTypes={eventTypes} onDuplicate={duplicateInstance} rowData={cell.row.original}/>
            }
        }
    ], [eventTypes, agents, props.isAdmin]);

    const isInstanceDeleted = (item: any) => {
        const reason = item?.instance?.reason || item.reason
        return reason === 'Deletion in progress';
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
                columns,
                data,
                initialState: {
                    hiddenColumns: ['id', 'instance'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                    sortBy: [{ id: 'name', desc: false }]
                }
            },
            useSortBy, usePagination);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, location.search]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    const startStopAll = async (isStart: boolean) => {
        const changedIds: number[] = [];

        const updatedInstances = instances.map((obj: InstanceTarget) => {
            const updatedObj = {
                ...obj,
                monitor: isStart,
                password: obj.password === '' ? null : obj.password,
                passwordchanged: false,
            };
            const isDeleted = isInstanceDeleted(updatedObj)

            if (updatedObj.monitor !== obj.monitor && !isDeleted) {
                changedIds.push(updatedObj.id);
            }

            return updatedObj;
        });
        const selectedInstances = updatedInstances.filter(instance =>
                changedIds.includes(instance.id)
        );
        try {
            if (changedIds.length) {
                await api.put('datasource', selectedInstances);
                changedIds.forEach(id => {
                    Helper.createInstanceEvent(
                            eventTypes,
                            id,
                            isStart ? 'Monitoring Enabled' : 'Monitoring Disabled',
                            isStart ? 'Enabling monitoring' : 'Stopping monitoring'
                    );
                });
                setInstances(updatedInstances);
            }
        } catch (error: any) {
            console.error('Failed to update monitor state on backend:', error);
            showError('Failed to update monitor state on backend', error.message || error.toString());
        }
    };

    return (
            <div className="col h-100">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-database fa-fw" aria-hidden="true"></i>
                        Monitored instances
                        <span className="badge bg-info"
                              data-tip="Total instance count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseInstancesAdmin" aria-expanded="false"
                           aria-controls="collapseInstancesAdmin"></i>
                    </div>
                    <div id="collapseInstancesAdmin" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <ConditionalRender if={props.isAdmin}>
                                    <button type="button" className='btn btn-sm btn-outline-secondary' data-tip='Start all'
                                            onClick={() => startStopAll(true)}>
                                        <i className='fal fa-play fa-fw'></i><span>Start all</span>
                                    </button>
                                    <button type="button" className='btn btn-sm btn-secondary' data-tip='Stop all'
                                            onClick={() => startStopAll(false)}>
                                        <i className='fal fa-stop fa-fw'></i><span>Stop all</span>
                                    </button>
                                </ConditionalRender>
                                <Link to="/admin/instances/create" role="button" className="btn btn-sm btn-success">
                                    <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                </Link>
                                <CSVLink role="button"
                                         headers={[{ label: 'ID', key: 'id' }, {
                                             label: 'Instance Name',
                                             key: 'name'
                                         }, { label: 'Type', key: 'type' }, {
                                             label: 'Host Address',
                                             key: 'hostAddress'
                                         }, { label: 'Last Contact', key: 'updated' }]}
                                         data={data}
                                         download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Instances.csv`}
                                         className="btn btn-sm btn-primary">
                                    <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                                </CSVLink>
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="table-responsive">
                            {isInitialLoad ? (
                                <div className="w-100 text-center text-muted my-3">
                                    <div className="loader spinner"></div>
                                    <p className="mt-3">Loading data...</p>
                                </div>
                            ) : (
                                <>
                                    <table {...getTableProps()} className="table">
                                        <thead>
                                        {headerGroups.map(headerGroup => (
                                                <tr {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map(column => (
                                                            <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                                className={(column as any).className}>
                                                                {column.render('Header')}
                                                                <SortClass column={column}/>
                                                            </th>
                                                    ))}
                                                </tr>
                                        ))}
                                        </thead>
                                        <tbody {...getTableBodyProps()}>
                                        {page.map(row => {
                                            prepareRow(row)
                                            return (
                                                    <tr {...row.getRowProps()}>
                                                        {row.cells.map((cell) => {
                                                            return (
                                                                    <td {...cell.getCellProps()} className={(cell.column as any).className}>
                                                                        {cell.render('Cell')}
                                                                    </td>
                                                            )
                                                        })}
                                                    </tr>
                                            )
                                        })}
                                        </tbody>
                                    </table>
                                    <NoData
                                            error={null}
                                            loading={false}
                                            length={data.length}
                                    />
                                </>
                            )}
                            <TablePagination
                                    length={data.length}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    canPreviousPage={canPreviousPage}
                                    previousPage={previousPage}
                                    canNextPage={canNextPage}
                                    nextPage={nextPage}
                                    gotoPage={gotoPage}
                                    pageIndex={pageIndex}
                            />
                        </div>
                    </div>
                </div>
            </div>
    )
}

export default List;
